import { React, useEffect, useState, useMemo, useCallback } from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import { multiSearch } from '../services';
import { useLocation } from 'react-router-dom';
import './DocList/DocCard.scss';
import { scoreColor } from '../utils';
import { useSelector } from 'react-redux';
import { selectAuthProfiles } from '../redux/selectors/authSelectors';
import CveVectors from './CveDocument/CveVectors';
import AlertMessage from './AlertMessage';
import ReadMoreDoc from './DocList/ReadMoreDoc';
import ReactPaginate from 'react-paginate';
import DocListSkeleton from './DocList/DocListSkeleton';
import { mitreDocTypes } from '../constants/constants';

const SearchResult = ({ searchState }) => {
  const profiles = useSelector(selectAuthProfiles);
  const searchParams = useLocation().search;
  const searchKeyword = new URLSearchParams(searchParams).get('searchKeyword');
  const PAGE_SIZE = 10;
  const [showImage, setShowImage] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [totalHits, setTotalHits] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);

  const hideImg = (event) => {
    setShowImage(false);
  };

  const repos = useMemo(() => {
    if (!profiles) return [];
    const repoList = Object.keys(profiles).map((p) => profiles[p].repositoryId);
    return repoList.filter((p, i) => p !== undefined && repoList.indexOf(p) === i);
  }, [profiles]);

  // Handle scroll to top when manually changing pages
  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
    window.scrollTo(0, 0);
  };

  const fetchPage = useCallback(
    async (pageIndex = 0) => {
      if (!searchKeyword || !repos.length) {
        setDocuments([]);
        setTotalHits(0);
        setPageCount(0);
        return;
      }

      setIsLoading(true);
      const firstResult = pageIndex * PAGE_SIZE + 1;

      try {
        const res = await multiSearch(repos, searchKeyword, null, firstResult, PAGE_SIZE);
        const docs = res?.documents || [];
        const total = res?.totalHits || 0;

        setDocuments(docs);
        setTotalHits(total);
        setPageCount(Math.ceil(total / PAGE_SIZE) || 0);
      } catch (error) {
        console.error('Failed to fetch search results', error);
        setDocuments([]);
        setTotalHits(0);
        setPageCount(0);
      } finally {
        setIsLoading(false);
      }
    },
    [PAGE_SIZE, repos, searchKeyword]
  );

  useEffect(() => {
    if (pageCount && currentPage > pageCount - 1) {
      setCurrentPage(pageCount - 1);
    }
  }, [pageCount, currentPage]);

  const whichProfile = (type) => {
    let primary = null;
    let secondaries = null;
    if (type === 'article') {
      primary = profiles.news;
      secondaries = [profiles.mitre, profiles.cve, profiles.podcasts];
    } else if (type === 'episode') {
      primary = profiles.podcasts;
      secondaries = [profiles.mitre, profiles.cve, profiles.news];
    } else if (mitreDocTypes.indexOf(type) > -1) {
      primary = profiles.mitre;
      secondaries = [profiles.podcasts, profiles.cve, profiles.news];
    } else if (type === 'vulnerability') {
      primary = profiles.cve;
      secondaries = [profiles.podcasts, profiles.mitre, profiles.news];
    }
    return {
      primary,
      secondaries,
    };
  };

  useEffect(() => {
    if (searchKeyword && profiles?.news?.repositoryId) {
      fetchPage(currentPage);
    }
  }, [searchKeyword, profiles?.news, fetchPage, currentPage]);

  const showingStart =
    documents.length && totalHits
      ? currentPage * PAGE_SIZE + 1
      : 0;
  const showingEnd = documents.length
    ? Math.min(totalHits, currentPage * PAGE_SIZE + documents.length)
    : 0;

  return (
    <>
      <Row style={{ background: '#eee' }} className='g-0'>
        <Col lg={12}>
          <div
            style={{
              padding: '20px',
              background: '#eee',
            }}
          >
            <h1
              style={{
                fontSize: '25px',
                background: '#eee',
                color: '#9f173e',
              }}
            >
              Search Results for "{searchKeyword}"
            </h1>

            {documents.length ? (
              <div>
                <span>
                  Showing {showingStart} - {showingEnd} of{' '}
                </span>
                <span>
                  <strong>{totalHits}</strong> Results
                </span>
              </div>
            ) : null}
          </div>
        </Col>
      </Row>
      <Row
        style={{
          background: '#eee',
        }}
        className='g-0'
      >
        <Col lg={12} className='px-2'>
          {!isLoading && documents.length ? (
            <>
              {documents.map((doc) => (
                <Card
                  className={`DocCard ${
                    doc.objectType === 'vulnerability' ? 'cve-doc-card' : ''
                  }`}
                  key={doc.id}
                  style={{
                    margin: '10px 0',
                  }}
                >
                  {/* <Card key={doc.id} style={{ margin: '10px auto', width: '90%' }}> */}
                  <Card.Body>
                    <div className='pill'>
                      {mitreDocTypes.indexOf(doc.objectType) > -1 ? (
                        <span>
                          Mitre:&nbsp;
                          <span className='mitre-doc-type'>
                            {doc.objectType}
                          </span>
                        </span>
                      ) : doc.objectType === 'vulnerability' ? (
                        <span className='cve-doc-type'>
                          critical vulnerability report
                        </span>
                      ) : doc.objectType === 'article' ? (
                        <span className='news-doc-type'>news article</span>
                      ) : doc.objectType === 'episode' ? (
                        <span className='news-doc-type'>podcast</span>
                      ) : (
                        <span className='doc-type'>{doc.objectType}</span>
                      )}
                    </div>
                    <div className='flex'>
                      {doc.imageUrl && showImage ? (
                        <Card.Img
                          variant='left'
                          src={doc.imageUrl}
                          onError={hideImg}
                        />
                      ) : null}
                      <section className='main-section'>
                        <Card.Title>{doc.title}</Card.Title>

                        <Card.Text className='ctx-card-info'>
                          {doc.objectType === 'vulnerability' &&
                          doc.cvssBaseScore ? (
                            <span className='ctx-field-group cve-base-score'>
                              <span className='ctx-label'>Base Score: </span>
                              <span className='ctx-value'>
                                <span
                                  className={`severity-coloring severity-coloring--${scoreColor(
                                    doc.cvssBaseScore,
                                    doc.cvssVersion
                                  )}`}
                                >
                                  {doc.cvssBaseScore}
                                </span>
                              </span>
                            </span>
                          ) : null}
                          {doc.cvssVector ? (
                            <span className='ctx-field-group cve-base-score'>
                              <span className='cve-label'>Vector: </span>
                              <span className='cve-value'>
                                <CveVectors vectors={doc.cvssVector} />
                              </span>
                            </span>
                          ) : null}
                          <span className='ctx-field-group'>
                            <span className='ctx-label'>Published On: </span>
                            <span className='ctx-value'>{doc.publishDate}</span>
                          </span>
                          {doc.showTitle ? (
                            <span className='ctx-field-group'>
                              <span className='ctx-label'>Podcast: </span>
                              <span className='ctx-value ctx-author'>
                                {doc.showTitle}
                              </span>
                            </span>
                          ) : null}
                          {doc.type !== 'cve' &&
                          doc.type !== 'podcast' &&
                          doc.type !== 'mitre' &&
                          doc.site ? (
                            <span className='ctx-field-group'>
                              <span className='ctx-label'>Source: </span>
                              <span className='ctx-value ctx-source'>
                                {doc.site}
                              </span>
                            </span>
                          ) : null}
                          {doc.author ? (
                            <span className='ctx-field-group'>
                              <span className='ctx-label'>Author: </span>
                              <span className='ctx-value ctx-author'>
                                {typeof doc.author === 'object'
                                  ? doc.author.map(
                                      (v, i) =>
                                        `${v}${
                                          doc.author.length !== i + 1 ? ',' : ''
                                        }`
                                    )
                                  : doc.author}
                              </span>
                            </span>
                          ) : null}
                        </Card.Text>
                      </section>
                    </div>

                    {profiles?.news?.repositoryId ? (
                      <ReadMoreDoc
                        doc={doc}
                        profilePrimary={whichProfile(doc.objectType).primary}
                        profileSecondary={
                          whichProfile(doc.objectType).secondaries
                        }
                        className='read-more-container'
                      />
                    ) : null}
                  </Card.Body>
                </Card>
              ))}
            </>
          ) : null}

          {isLoading ? <DocListSkeleton />
          : null}
        </Col>
      </Row>
      <Row className='g-0'>
        <Col lg={12} className='mt-4'>
          {documents.length === 0 && isLoading === false ? (
            <AlertMessage variant='danger' message='No Record Found!' />
          ) : null}
        </Col>
      </Row>
      <Row className='g-0'>
        <Col lg={12}>
          {pageCount > 1 ? (
            <ReactPaginate
              breakLabel='...'
              nextLabel='Next'
              onPageChange={handlePageChange}
              pageRangeDisplayed={3}
              marginPagesDisplayed={1}
              pageCount={pageCount}
              previousLabel='Previous'
              forcePage={currentPage}
              containerClassName='pagination justify-content-center ctx-pagination'
              pageClassName='page-item'
              pageLinkClassName='page-link'
              previousClassName='page-item'
              previousLinkClassName='page-link'
              nextClassName='page-item'
              nextLinkClassName='page-link'
              activeClassName='active'
              disabledClassName='disabled'
            />
          ) : null}
        </Col>
      </Row>
    </>
  );
};
export default SearchResult;
